// The setpoint dial's geometry — ported verbatim (the maths, not the RN/SVG
// plumbing) from app/src/components/Radial/index.js so the web dial behaves
// exactly like the mobile one. These functions are framework-agnostic (pure
// trigonometry over a 380×380 viewBox), so they are the correctness core of the
// dial and are unit-tested in isolation.
//
// The dial is a 280° arc with an 80° gap at the bottom: angles are measured from
// the bottom (circleOrigin = 180) so the usable range never crosses 360. A
// temperature maps to an angle in [centerOffset, 360 − centerOffset] and back.

export const trackRadius = 132;
export const center = { x: 190, y: 190 };

// The arc extremes are FIXED at 5 °C and 30 °C regardless of the zone's frost
// floor or limit locks: the arc geometry, the dot positions and the min/max
// labels are all drawn over this range, while the zone's minSetpoint /
// maxSetpoint only clamp what the user is allowed to SELECT. Passing these to
// the geometry functions (in place of the zone's own min/max) is what makes the
// dial read identically for every zone.
export const ARC_MIN_SETPOINT = 500;
export const ARC_MAX_SETPOINT = 3000;

export const innerCircleRadius = 103;
export const outerCircleRadius = 120;
export const disabledOverlayCircleRadius = 170;

// Angles start at the bottom so the usable range doesn't cross 360.
const circleOrigin = 180;

export const centerOffset = 40;
export const startPointAngle = circleOrigin + centerOffset; // 220
export const endPointAngle = circleOrigin + 360 - centerOffset; // 500

export const radialMarkerRadius = 5;
export const enlargedRadialMarkerRadius = 8;

// How close (in degrees) a pointer-down must be to the setpoint dot to start a drag.
export const setpointTouchThreshold = 20;
// The furthest from centre a drag may start (just outside the track).
export const maxDragStartDistance = trackRadius + 60; // 192

export function clampNumber(temperature, minSetpoint, maxSetpoint) {
  return Math.max(Math.min(maxSetpoint, temperature), minSetpoint);
}

// Snap a centidegree value to the nearest step (50 = 0.5 °C) — the dial's grid.
export function snapToStep(centi, step = 50) {
  return Math.round(centi / step) * step;
}

export function distanceFromCenter(x, y, origin = center) {
  return Math.sqrt(Math.pow(origin.x - x, 2) + Math.pow(origin.y - y, 2));
}

// A point (in viewBox coords) → the dial angle in degrees (0 at the bottom).
export function cartesianToPolarAngle(x, y, origin = center) {
  const radianToDegrees = 180 / Math.PI;

  const xxx = origin.x - x;
  const yyy = origin.y - y;
  const radians = Math.atan2(yyy, xxx);

  let offset = radians + Math.PI / 2;
  if (offset < 0) {
    offset += 2 * Math.PI;
  }

  return (offset % (2 * Math.PI)) * radianToDegrees;
}

// A dial angle → the point on the track circle (viewBox coords).
export function polarToCartesian(angleToChange, origin = center) {
  const r = trackRadius;
  const a = ((angleToChange - 90) * Math.PI) / 180.0;
  const x = r * Math.cos(a) + origin.x;
  const y = r * Math.sin(a) + origin.y;
  return { x, y };
}

export const startCoord = polarToCartesian(startPointAngle, center);

// A pointer angle → the temperature it represents (clamped to the usable arc).
export function temperatureForAngle(angle, minSetpoint, maxSetpoint) {
  const tempRange = maxSetpoint - minSetpoint;
  const angleRange = 360 - centerOffset * 2; // 280
  const stepDegrees = tempRange / angleRange;

  const c = clampNumber(angle, centerOffset, 360 - centerOffset) - centerOffset;

  return c * stepDegrees + minSetpoint;
}

// A temperature → its angle along the arc (before the start offset is applied).
export function angleForTemperature(temperature, minSetpoint, maxSetpoint) {
  const stepDegrees = (360 - centerOffset * 2) / (maxSetpoint - minSetpoint);
  return stepDegrees * (temperature - minSetpoint);
}

// Whether a pointer angle is within the grab threshold of the setpoint dot.
export function isNearSetpoint(touchAngle, setpoint, minSetpoint, maxSetpoint) {
  const setpointAngle =
    angleForTemperature(setpoint, minSetpoint, maxSetpoint) + centerOffset;
  const diff = Math.abs(touchAngle - setpointAngle);
  const wrappedDiff = Math.min(diff, 360 - diff);
  return wrappedDiff <= setpointTouchThreshold;
}

// The point on the track for a given temperature (dot positions).
export function getSetpointCoord(temperature, minSetpoint, maxSetpoint) {
  const setpointAngle = angleForTemperature(
    temperature,
    minSetpoint,
    maxSetpoint,
  );
  return polarToCartesian(setpointAngle + centerOffset + circleOrigin, center);
}

// The SVG arc path from the start of the track to a given temperature.
export function pathForTemperatureArc(temperature, minSetpoint, maxSetpoint) {
  const clamped = clampNumber(temperature, minSetpoint, maxSetpoint);
  const angle = angleForTemperature(clamped, minSetpoint, maxSetpoint);
  const setpointCoord = polarToCartesian(
    angle + centerOffset + circleOrigin,
    center,
  );

  const m = `M${startCoord.x} ${startCoord.y}`;
  // A: rx ry x-axis-rotation large-arc-flag sweep-flag x y
  const a = `A ${trackRadius} ${trackRadius} 0 ${angle > 180 ? 1 : 0} 1 ${
    setpointCoord.x
  } ${setpointCoord.y}`;
  return `${m} ${a}`;
}
